import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPatch } from '../api/client';
import { useEntity } from './EntityContext';
import { DEFAULT_MODULES, MODULE_KEYS, type ModuleKey } from '../config/modules';

type ModulesContextValue = {
  modules: Record<ModuleKey, boolean>;
  loading: boolean;
  isModuleEnabled: (key: ModuleKey) => boolean;
  setModuleEnabled: (key: ModuleKey, enabled: boolean) => Promise<void>;
};

const ModulesContext = createContext<ModulesContextValue | null>(null);

function normalizeModules(raw: Partial<Record<ModuleKey, boolean>> | undefined): Record<ModuleKey, boolean> {
  const next = { ...DEFAULT_MODULES };
  if (!raw) return next;
  for (const key of MODULE_KEYS) {
    if (typeof raw[key] === 'boolean') next[key] = raw[key] as boolean;
  }
  return next;
}

export function ModulesProvider({ children }: { children: React.ReactNode }) {
  const { currentEntityId } = useEntity();
  const [modules, setModules] = useState<Record<ModuleKey, boolean>>(DEFAULT_MODULES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentEntityId) {
      setModules(DEFAULT_MODULES);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiGet<{ modules?: Partial<Record<ModuleKey, boolean>> }>('/api/v1/settings')
      .then((res) => {
        if (!cancelled) setModules(normalizeModules(res.modules));
      })
      .catch(() => {
        if (!cancelled) setModules(DEFAULT_MODULES);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentEntityId]);

  const isModuleEnabled = useCallback((key: ModuleKey) => modules[key] !== false, [modules]);

  const setModuleEnabled = useCallback(async (key: ModuleKey, enabled: boolean) => {
    const previous = modules;
    const next = { ...modules, [key]: enabled };
    setModules(next);
    try {
      const res = await apiPatch<{ modules?: Partial<Record<ModuleKey, boolean>> }>('/api/v1/settings', {
        modules: { [key]: enabled },
      });
      setModules(normalizeModules(res.modules));
    } catch (err) {
      setModules(previous);
      throw err;
    }
  }, [modules]);

  const value = useMemo(
    () => ({ modules, loading, isModuleEnabled, setModuleEnabled }),
    [modules, loading, isModuleEnabled, setModuleEnabled],
  );

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) throw new Error('useModules must be used within ModulesProvider');
  return ctx;
}
